import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { worldCountries } from '@/data/countries/index';
import { Loader2, Database, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const CityDataSync = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [progress, setProgress] = useState(0);

  const totalCountries = worldCountries.length;
  const totalCities = worldCountries.reduce((acc, c) => acc + c.topCities.length, 0);

  const syncCities = async () => {
    setIsLoading(true);
    setProgress(0);
    setResult(null);

    try {
      toast.info('Iniciando sincronización de ciudades...');
      
      // Simulate progress (since the actual sync happens on the server)
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 5;
        });
      }, 500);

      const { data, error } = await supabase.functions.invoke(
        'sync-cities-to-database',
        { 
          body: { 
            countries: worldCountries.map(country => ({
              name: country.name,
              slug: country.slug,
              code: country.code,
              topCities: country.topCities
            }))
          } 
        }
      );

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        console.error('Error:', error);
        toast.error('Error en la sincronización');
        setResult({ success: false, error: error.message });
      } else {
        console.log('✅ Sync completado:', data);
        toast.success(`✅ ${data.inserted} ciudades sincronizadas`);
        setResult(data);
      }
    } catch (err: any) {
      console.error('Exception:', err);
      toast.error('Error crítico en la sincronización');
      setResult({ success: false, error: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <Card className="max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <Database className="h-8 w-8 text-primary" />
            <CardTitle className="text-3xl">City Database Sync</CardTitle>
          </div>
          <CardDescription className="text-lg">
            Migrar todas las ciudades de worldCountries a la base de datos Supabase
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Statistics */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Países en código</p>
              <p className="text-2xl font-bold">{totalCountries.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ciudades a sincronizar</p>
              <p className="text-2xl font-bold text-primary">{totalCities.toLocaleString()}</p>
            </div>
          </div>

          {/* Progress */}
          {isLoading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Sincronizando ciudades...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Action Button */}
          <Button 
            onClick={syncCities} 
            disabled={isLoading}
            size="lg"
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Sincronizando {progress}%...
              </>
            ) : (
              <>
                <Database className="mr-2 h-5 w-5" />
                Iniciar Sincronización
              </>
            )}
          </Button>

          {/* Results */}
          {result && (
            <Card className={result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <CardTitle className={result.success ? 'text-green-900' : 'text-red-900'}>
                    {result.success ? 'Sincronización Exitosa' : 'Error en Sincronización'}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium">Total ciudades:</span>
                    <span>{result.totalCities?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Insertadas/Actualizadas:</span>
                    <span className="text-green-600 font-bold">{result.inserted?.toLocaleString() || 0}</span>
                  </div>
                  {result.errors > 0 && (
                    <div className="flex justify-between">
                      <span className="font-medium">Errores:</span>
                      <span className="text-red-600 font-bold">{result.errors}</span>
                    </div>
                  )}
                  {result.message && (
                    <p className="mt-4 p-3 bg-white rounded border">
                      {result.message}
                    </p>
                  )}
                  {result.errorDetails && result.errorDetails.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <span className="font-medium text-red-900">Detalles de errores:</span>
                      </div>
                      <div className="max-h-40 overflow-y-auto bg-white p-3 rounded border space-y-1">
                        {result.errorDetails.map((error: string, idx: number) => (
                          <p key={idx} className="text-xs text-red-700">{error}</p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Información importante:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Este proceso insertará/actualizará {totalCities.toLocaleString()} ciudades en la base de datos</li>
                  <li>Las ciudades duplicadas (mismo slug) serán actualizadas</li>
                  <li>El proceso puede tomar 2-5 minutos</li>
                  <li>Se procesarán en lotes de 1,000 ciudades</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CityDataSync;
